"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, SlidersHorizontal } from "lucide-react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import type { SavedViewConfig } from "../../../UniversalCRMTable/types";

interface ManageViewsDialogProps {
  /** All views eligible to be shown/hidden/reordered (config presets + user-created). */
  views: SavedViewConfig[];
  /** Canonical full order of view ids (Main excluded — Main is always pinned first). */
  orderedIds: string[];
  onReorder: (orderedIds: string[]) => void;
  isHidden: (viewId: string) => boolean;
  onHide: (viewId: string) => void;
  onUnhide: (viewId: string) => void;
  /** The active view id — its visibility is locked on (see filterVisibleTabs). */
  activeViewId: string | null;
}

interface SortableViewRowProps {
  id: string;
  name: string;
  hidden: boolean;
  isActive: boolean;
  onToggle: () => void;
}

function SortableViewRow({
  id,
  name,
  hidden,
  isActive,
  onToggle,
}: SortableViewRowProps): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-2"
    >
      <button
        type="button"
        className="touch-manipulation cursor-grab active:cursor-grabbing rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={`Reorder ${name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span
        className={`flex-1 truncate text-sm ${
          hidden ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {name}
        {isActive && (
          <span className="ml-1.5 text-[10px] text-muted-foreground">(active)</span>
        )}
      </span>
      <button
        type="button"
        onClick={onToggle}
        disabled={isActive}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        aria-label={hidden ? `Show ${name} in tab bar` : `Hide ${name} from tab bar`}
        title={
          isActive
            ? "The active view is always shown"
            : hidden
              ? "Show in tab bar"
              : "Hide from tab bar"
        }
      >
        {hidden ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4 text-blue-600" />
        )}
      </button>
    </div>
  );
}

/**
 * Dialog at the right of the saved-views tab bar. Lets the user drag view pills
 * into the left-to-right order they want and hide/show individual views. Both are
 * pure per-user presentation preferences (server-persisted via
 * useSavedViewTabPrefs) — neither changes the views themselves or how filtering
 * works.
 */
export const ManageViewsDialog: React.FC<ManageViewsDialogProps> = ({
  views,
  orderedIds,
  onReorder,
  isHidden,
  onHide,
  onUnhide,
  activeViewId,
}) => {
  const [open, setOpen] = useState(false);

  const viewsById = useMemo(() => {
    const map = new Map<string, SavedViewConfig>();
    views.forEach((v) => map.set(v.id, v));
    return map;
  }, [views]);

  // Render rows in the persisted order, appending any views missing from it.
  const rowIds = useMemo(() => {
    const known = orderedIds.filter((id) => viewsById.has(id));
    const knownSet = new Set(known);
    const missing = views.filter((v) => !knownSet.has(v.id)).map((v) => v.id);
    return [...known, ...missing];
  }, [orderedIds, views, viewsById]);

  // Local working order so drags feel instant; committed to storage on drop.
  const [localOrder, setLocalOrder] = useState<string[]>(rowIds);
  useEffect(() => {
    if (open) setLocalOrder(rowIds);
  }, [open, rowIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setLocalOrder((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return prev;
        const next = arrayMove(prev, oldIndex, newIndex);
        onReorder(next);
        return next;
      });
    },
    [onReorder]
  );

  if (views.length === 0) return null;

  const hiddenCount = views.filter((v) => isHidden(v.id)).length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="h-5 flex items-center gap-1 px-1.5 flex-shrink-0 rounded-full text-2xs text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Manage views"
        title="Reorder, show, or hide views"
      >
        <SlidersHorizontal className="h-3 w-3" />
        {hiddenCount > 0 && (
          <span className="font-medium tabular-nums">{hiddenCount}</span>
        )}
      </button>
      <ResponsiveModal forceModal
        open={open}
        onOpenChange={setOpen}
        title="Manage views"
        description="Drag to reorder the view tabs, or toggle which ones show in the bar. Saved on this device only."
        maxWidth="md"
      >
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
            <div className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto py-1">
              {localOrder.map((id) => {
                const view = viewsById.get(id);
                if (!view) return null;
                const hidden = isHidden(id);
                const isActive = activeViewId === id;
                return (
                  <SortableViewRow
                    key={id}
                    id={id}
                    name={view.name}
                    hidden={hidden}
                    isActive={isActive}
                    onToggle={() => (hidden ? onUnhide(id) : onHide(id))}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </ResponsiveModal>
    </>
  );
};
